
import { Component, Vue } from 'vue-property-decorator';
import { NavBar, Tab } from '@warrenbrasil/nebraska-web';
import { BackButton } from '@/modules/common/components/buttons/BackButton';
import { TransactionList } from './components/TransactionList';
import { TransactionsTable } from './components/TransactionsTable';
import { WarrenModules } from '@/types/navigation';
import { getNavigation, Menu } from './helpers/navigation';
import { TransactionModal } from './components/TransactionModal';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';
import { FeatureFlags } from '@/types/models/FeatureFlags';
import { Balances } from './components/Balances';
import {
  trackReloadBalanceAccountErrorClick,
  trackBalanceAccountErrorLoad
} from '@/modules/home/components/HomeCash/track-events';
import { OpenTransactionEventPayload, IBalances } from './types';
import { ErrorHandler } from '@/modules/common/components/ErrorHandler';
import {
  trackPageLoad,
  trackPageBackClick,
  trackChangeTabClick
} from './helpers/track-events';
import {
  trackTransactionsModuleChange,
  trackTransactionDetails
} from './helpers/transactions-events';
import {
  getCashBalances,
  ICashBalancesResponse
} from '@/modules/transaction/services/unified-account';

@Component({
  components: {
    NavBar,
    Tab,
    BackButton,
    TransactionList,
    TransactionModal,
    TransactionsTable,
    Balances,
    ErrorHandler
  }
})
export default class TransactionsView extends Vue {
  private transactionId = '';
  private transactionType = '';
  private menu: Menu[] = [];
  private showModal = false;
  private module: WarrenModules = WarrenModules.Cash;
  balances: Reactive<ICashBalancesResponse> = null;
  isLoading = false;
  hasError = false;
  WarrenModules = WarrenModules;
  reloadTable = false;

  created() {
    this.loadCash();
    const moduleFromQuery = this.$route.query.module as WarrenModules;

    this.menu = getNavigation();

    if (moduleFromQuery) {
      this.module =
        moduleFromQuery === WarrenModules.Home
          ? WarrenModules.Cash
          : moduleFromQuery;
    }

    trackPageLoad();
  }

  reloadCash() {
    this.loadCash();
    trackReloadBalanceAccountErrorClick();
  }

  async loadCash() {
    this.isLoading = true;
    this.hasError = false;
    try {
      const parsedBalances = await getCashBalances();
      this.balances = parsedBalances;
    } catch {
      this.hasError = true;
      trackBalanceAccountErrorLoad();
    } finally {
      this.isLoading = false;
    }
  }

  get isAccountProcessingWarningEnabled() {
    return FeatureFlagsModule.isEnabled(
      FeatureFlags.ACCOUNT_PROCESSING_WARNING
    );
  }

  get balance() {
    return this.parsedBalances?.netBalance || '';
  }

  get balanceLastUpdatedAt() {
    return this.parsedBalances?.balanceLastUpdatedAtFormatted
      ? `Atualizado em ${this.parsedBalances?.balanceLastUpdatedAtFormatted}`
      : '';
  }

  // Foi removido temporariamente o valor passado do this.newCashData?.processingWarning,
  // pois o time de pagamentos irá desenhar a melhor forma para utilizar essa informação
  get warningText() {
    return this.isAccountProcessingWarningEnabled ? '' : '';
  }

  get parsedBalances(): IBalances | null {
    return {
      total: this.balances?.cashBalances.totalBalance || '',
      grossEarnings: this.balances?.cashBalances.currentEarnings || '',
      incomeTax: this.balances?.cashBalances.incomeTax || '',
      transactionTax: this.balances?.cashBalances.transactionTax || '',
      netBalance: this.balances?.cashBalances.netBalance || '',
      marketIndexMessage: this.balances?.cashBalances.marketIndexMessage || '',
      administrationTax: this.balances?.cashBalances.administrationTax || '',
      balanceLastUpdatedAtFormatted:
        this.balances?.cashBalances.balanceLastUpdatedAtFormatted || ''
    };
  }

  private openTransactionModal(transaction: OpenTransactionEventPayload) {
    this.transactionType = transaction.typeV2 || transaction.type;
    this.transactionId = transaction.id;
    this.showModal = true;

    trackTransactionDetails(this.module, transaction.type);
  }

  private closeTransactionModal() {
    this.transactionId = '';
    this.showModal = false;
    this.reloadTable = !this.reloadTable;
    this.loadCash();
  }

  private get shouldShowNewBankStatement() {
    const isFeatureEnabled = FeatureFlagsModule.isEnabled(
      FeatureFlags.ENABLE_BANK_STATEMENT_V2
    );
    const isTradeModule = this.module === WarrenModules.Trade;
    return isFeatureEnabled && !isTradeModule;
  }

  private changeModule({ module }: Menu) {
    this.module = module;

    if (module !== this.$route.query?.module) {
      this.$router.replace({
        name: 'transactions',
        query: { ...this.$route.query, module }
      });

      trackTransactionsModuleChange(module);
      trackChangeTabClick(this.module);
    }
  }

  private onBackClick() {
    this.$router.back();
    trackPageBackClick();
  }
}
